import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/common/Button";
import { Modal } from "components/common/Modal";
import type { ConfirmActionModalProps } from "./types";

const confirmButtonVariantByAction: Record<
  ConfirmActionModalProps["action"],
  "success" | "danger" | "warning" | "primary"
> = {
  approve: "success",
  unapprove: "danger",
  delete: "danger",
  archive: "warning",
  move: "primary",
  undo: "primary",
  detect: "warning",
};

export const ConfirmActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  title,
  description,
}: ConfirmActionModalProps) => {
  const { t } = useTranslation();

  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // The caller reports the failure; staying open lets the user retry or cancel.
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      disableBackdropClose={isConfirming}
      disableEscapeClose={isConfirming}
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-sm text-gray-600 mb-6">{description}</p>
      <div className="flex justify-end gap-3">
        <Button onClick={onClose} disabled={isConfirming}>
          {t("smartSurveyor.common.cancel")}
        </Button>
        <Button
          variant={confirmButtonVariantByAction[action]}
          onClick={handleConfirm}
          disabled={isConfirming}
          startAdornment={
            isConfirming ? <Loader2 size={14} className="animate-spin" /> : null
          }
        >
          {t("smartSurveyor.common.confirm")}
        </Button>
      </div>
    </Modal>
  );
};
